import NoResultIcon from '@/common/assets/icons/NoResultIcon.svg?react';
import NoResultGeneral from '@/common/assets/icons/noResultGeneral.svg?react';
import CircularLoading from '@/common/components/base/Loading/CircularLoading.tsx';
import Input from '@/common/components/base/input/base/Input.tsx';
import createAttachment from '@/common/components/base/input/base/helpers/createAttachment';
import { ModalInputType } from '@/common/components/base/input/modalInput/ModalInput.type.ts';
import ArrowDownAttachment from '@/common/components/base/input/modalInput/attachments/ArrowDownAttachment';
import LoadingAttachment from '@/common/components/base/input/modalInput/attachments/LoadingAttachment';
import TextInput from '@/common/components/base/input/textInput/TextInput.tsx';
import MainModal from '@/common/components/base/modal/Modal.tsx';
import TitleDescription from '@/common/components/base/typography/TitleDescription';
import Typography from '@/common/components/base/typography/Typography.tsx';
import useModal from '@/common/hooks/useModal.ts';
import { SelectionOptionsType } from '@/common/types/SelectionOptions.type.ts';
import { requiredText } from '@/common/utils/rules/publicRules';
import { forwardRef, ForwardRefRenderFunction } from 'react';
import {
  FormProvider,
  useController,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { css } from 'styled-system/css';
import { Box } from 'styled-system/jsx';

const ModalInputComponent: ForwardRefRenderFunction<
  HTMLInputElement,
  ModalInputType
> = (
  {
    name,
    disabled,
    defaultValue = '',
    label,
    rules,
    placeholder,
    options,
    onChange,
    loading,
    searchOptions,
    endIcon,
    endAttachments,
    children,
    inputProps,
    optionalLabel,
    hasLabel,
    hasErrorMassage,
    title,
    noResultForLocation = false,
  },
  ref,
) => {
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    rules,
    control,
    name,
    defaultValue,
  });

  const { openModal, open, closeModal } = useModal({
    onModalClose: () => {
      searchOptions?.onChange?.('');
    },
  });

  const errorMessage = fieldState.error?.message;

  const handleSelect = (option: SelectionOptionsType<any>) => {
    field.onChange(option);
    onChange?.(option);
    closeModal();
  };

  const finalAttachment =
    endAttachments ??
    (endIcon
      ? [
          createAttachment({
            element: endIcon,
            shouldRender: true,
            size: 'medium',
          }),
        ]
      : loading
        ? [LoadingAttachment]
        : [ArrowDownAttachment]);

  return (
    <>
      <Input
        inputProps={{
          value: field.value?.label ?? '',
          placeholder,
          disabled,
          readOnly: true,
          onFocus: openModal,
          ...inputProps,
        }}
        hasLabel={hasLabel}
        hasErrorMassage={hasErrorMassage}
        optionalLabel={optionalLabel}
        inputRef={(element) => {
          field.ref(element);
          if (typeof ref === 'function') ref(element);
          else if (ref) ref.current = element;
        }}
        errorMessage={errorMessage}
        label={label}
        endAttachments={finalAttachment}
      />

      <MainModal open={open} closeModal={closeModal}>
        {title && (
          <TitleDescription
            textStyle="h2"
            className={css({
              marginBottom: '24px',
              position: 'relative',
              marginRight: '-10px',
            })}
            color="black-1"
          >
            {title}
          </TitleDescription>
        )}

        {searchOptions && <ModalInputSearch {...searchOptions} />}

        {loading ? (
          <Box display="flex" justifyContent="center" mt="50px">
            <CircularLoading color="colors.purple-primary" />
          </Box>
        ) : (
          (children?.(closeModal) ?? (
            <OptionModal
              noResultForLocation={noResultForLocation}
              onClick={handleSelect}
              options={options}
            />
          ))
        )}
      </MainModal>
    </>
  );
};

const ModalInput = forwardRef(ModalInputComponent);
export default ModalInput;

// -------------------- Option Modal -----------------------

type OptionModalProps = {
  options?: SelectionOptionsType[];
  onClick: (option: SelectionOptionsType) => void;
  noResultForLocation?: boolean;
};

const OptionModalComponent: ForwardRefRenderFunction<
  HTMLDivElement,
  OptionModalProps
> = ({ options, onClick, noResultForLocation = false }, ref) => {
  return (
    <Box className={css({ maxHeight: '60vh', overflowY: 'auto' })}>
      {options?.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          mt="50px"
        >
          {!noResultForLocation ? <NoResultGeneral /> : <NoResultIcon />}
          <Typography
            textStyle="b2"
            textAlign="center"
            py="13px"
            color="black-3"
          >
            نتیجه‌ای یافت نشد !
          </Typography>
        </Box>
      ) : (
        options?.map((option) => (
          <Typography
            onClick={() => onClick(option)}
            key={option.value}
            textStyle="b2"
            py="13px"
            borderBottom="1px solid #f0f0f0"
            cursor="pointer"
            ref={ref}
          >
            {option.label}
          </Typography>
        ))
      )}
    </Box>
  );
};

const OptionModal = forwardRef(OptionModalComponent);

// -------------------- Search Input -----------------------

const ModalInputSearch = (props: ModalInputType['searchOptions']) => {
  const form = useForm();
  return (
    <Box>
      <FormProvider {...form}>
        <TextInput
          rules={{ required: requiredText }}
          name="search"
          {...props!}
        />
      </FormProvider>
    </Box>
  );
};
